import { motion, AnimatePresence } from "framer-motion";
import { Ticket, X } from "lucide-react";
import { fmtDate, fmtOdds, fmtTokens } from "../lib/format";

const statusBadge = (status) => {
  if (status === "won") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (status === "lost") return "bg-red-50 text-red-700 border-red-200";
  return "bg-blue-50 text-blue-700 border-blue-200";
};
const statusLabel = (s) => (s === "won" ? "Vunnen" : s === "lost" ? "Förlorad" : "Aktiv");

export default function CouponReceipt({ coupon, onClose }) {
  return (
    <AnimatePresence>
      {coupon && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={onClose}
          data-testid="coupon-receipt-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
            data-testid="coupon-receipt"
          >
            <div className="bg-slate-900 px-7 py-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white">
                  <Ticket size={18} />
                </div>
                <div>
                  <p className="font-display font-bold text-white text-lg leading-tight">Kupongkvitto</p>
                  <p className="text-[11px] text-slate-400 font-mono2">#{coupon.id.slice(-8).toUpperCase()}</p>
                </div>
              </div>
              <button onClick={onClose} data-testid="receipt-close-button" className="text-slate-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-7">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Spelare</p>
                  <p className="font-display font-bold text-lg" data-testid="receipt-player-name">{coupon.player_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Lagd</p>
                  <p className="text-sm font-medium">{fmtDate(coupon.created_at)}</p>
                </div>
              </div>

              <div className="space-y-2 mb-5">
                {coupon.legs.map((leg, j) => {
                  const marketLabel = leg.market_label || (leg.pick === "over" || leg.pick === "under" ? "Över/Under mål" : "Fulltidsresultat");
                  const displayPick = leg.label || (leg.pick === "over" ? "Över" : leg.pick === "under" ? "Under" : leg.pick);
                  return (
                    <div key={j} className="bg-[#FAFAFC] border border-slate-200 rounded-xl px-4 py-2.5">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span>{leg.home_team} mot {leg.away_team}</span>
                        <span className="font-mono2 font-semibold">{displayPick} · {fmtOdds(leg.odds)}</span>
                      </div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">{marketLabel}</p>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-dashed border-slate-200 pt-4 flex flex-wrap gap-6">
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-400">Insats</p>
                  <p className="font-mono2 font-bold" data-testid="receipt-stake">{fmtTokens(coupon.stake)} tokens</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-400">Totalodds</p>
                  <p className="font-mono2 font-bold" data-testid="receipt-odds">{fmtOdds(coupon.total_odds)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-400">
                    {coupon.status === "active" ? "Möjlig vinst" : "Utbetalning"}
                  </p>
                  <p className={`font-mono2 font-bold ${coupon.status === "won" ? "text-emerald-600" : coupon.status === "lost" ? "text-red-500" : "text-blue-600"}`} data-testid="receipt-payout">
                    {fmtTokens(coupon.status === "active" ? coupon.potential_return : coupon.payout)} tokens
                  </p>
                </div>
              </div>

              <div className="mt-5 flex justify-center">
                <span className={`text-xs font-semibold border px-4 py-1.5 rounded-full ${statusBadge(coupon.status)}`} data-testid="receipt-status">
                  {statusLabel(coupon.status)}
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
